import CreditCardIcon from '@mui/icons-material/CreditCard';
import {
  Grid,
  List,
  Paper,
  Typography,
  Stack,
  Chip,
  Divider,
  TextField,
} from '@mui/material';
import { SearchFilter } from 'components/ui/SearchFilter';
import { SingleOrderCompanyView } from 'pages/Orders/Components/SingleOrderCompanyView';
import ownerAtom from 'data/atoms/ownerAtom';
import userAtom from 'data/atoms/userAtom';
import useWindowsDimension from 'functions/useWindowsDimension';
import { useAtom } from 'jotai';
import { useEffect, useReducer, useState } from 'react';
import {
  initialRootOrderState,
  RootOrderReducer,
} from './Reducers/RootOrderReducer';
import { useGet } from 'http/useApi';
import { rq } from 'http/apiRoutes';
import {
  OrderedOrderHeaderViewModel,
  OrderedPageViewModel,
  OrderedPurchaseLineViewModel,
} from './models/orderedModel';
import OrderedLeftSideItem from './ViewModels/OrderedLeftSideItem';
import { SingleOrderEmployeeView } from './Components/SingleOrderEmployeeView';
import CustomAvatar from 'components/ui/customAvatar';
import { t } from 'i18next';

/* -------------------------------------------------------------------------- */
/*                             READ-ONLY RIGHT SIDE                           */
/* -------------------------------------------------------------------------- */

type RightProps = {
  order: OrderedOrderHeaderViewModel | null;
  loading?: boolean;
};

/** Rest = ordered - received (aldri negativ) */
const remainingQty = (line: OrderedPurchaseLineViewModel) =>
  Math.max(
    0,
    (Number(line.orderedQuantity) || 0) - (Number(line.receivedQuantity) || 0)
  );

const formatCurrency = (n?: number | null) => `${(n ?? 0).toFixed(2)} NOK`;

// Robust lineId
const getLineId = (line: OrderedPurchaseLineViewModel): string =>
  ((line as any).orderPurchaseLineId ??
    (line as any).orderPostId ??
    (line as any).id ??
    '') as string;

export const OrderedHistoryRightSideItem: React.FC<RightProps> = ({
  order,
  loading = false,
}) => {
  const { height } = useWindowsDimension();

  if (!order) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant='h6'>Ingen ordre valgt</Typography>
        <Typography variant='body2' sx={{ opacity: 0.8 }}>
          Velg en ordre i listen til venstre for å se detaljer.
        </Typography>
      </Paper>
    );
  }

  const allDelivered = (order.lines ?? []).every((l) => remainingQty(l) === 0);

  return (
    <Stack spacing={2}>
      {/* Frakt & referanser (READ-ONLY) */}
      <Paper sx={{ p: 2, borderRadius: 3, backgroundColor: 'primary.main' }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
          Frakt & referanser (historikk)
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              disabled
              label='Trackingnummer'
              value={order.trackingNo ?? ''}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              disabled
              label='Leverandørens ordre-nummer'
              value={order.vendorOrderNumber ?? ''}
              InputProps={{ readOnly: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Linjer */}
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
          Linjer ({order.lines?.length ?? 0})
        </Typography>
        <Divider sx={{ my: 1 }} />

        <Stack spacing={1.5} sx={{ height: height - 700, overflowY: 'auto' }}>
          {(order.lines ?? []).map((line) => {
            const ordered = Number(line.orderedQuantity) || 0;
            const delivered = Number(line.receivedQuantity) || 0;
            const rest = remainingQty(line);

            const partial = delivered > 0 && rest > 0;
            const done = rest === 0 && ordered > 0;

            const title =
              line.companyName ??
              (line.ticketNo ? `Ticket: ${line.ticketNo}` : 'Linje');

            return (
              <Paper
                key={line.orderPurchaseLineId}
                sx={{ p: 1, borderRadius: 2, backgroundColor: 'primary.main' }}
              >
                <Grid container spacing={1}>
                  <Grid
                    container
                    spacing={1}
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{ backgroundColor: 'transparent' }}
                  >
                    {/* Enkel “avatar”/placeholder */}
                    <Grid item>
                      <CustomAvatar
                        avatarName={title}
                        imgWidth={120}
                        imgHeight={120}
                      />
                    </Grid>

                    {/* Midtpanel */}
                    <Grid item sx={{ flexGrow: 1, textAlign: 'left' }}>
                      <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography
                          sx={{
                            fontSize: 20,
                            flexGrow: 1,
                            fontWeight: 'bold',
                            letterSpacing: 1,
                          }}
                        >
                          {title}
                        </Typography>
                      </Stack>

                      {/* Eier/kontakt finnes ikke på linje – vis metadata vi har */}
                      <Grid
                        container
                        spacing={1}
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Grid item>
                          {/* Ticket / Company småfelt */}
                          <Typography sx={{ fontSize: 16, opacity: 0.8 }}>
                            {line.ticketNo ? `Ticket: ${line.ticketNo}` : ''}
                          </Typography>
                        </Grid>

                        <Grid item sx={{ flexGrow: 1, textAlign: 'left' }}>
                          {/* Linje 1: mengde (delivered/ordered) */}
                          <Stack direction='row' spacing={0}>
                            <Typography
                              sx={{
                                fontSize: 16,
                                flexGrow: 1,
                                maxWidth: '62%',
                                minWidth: '62%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {line.companyName ?? ''}
                            </Typography>

                            <Stack
                              direction='row'
                              spacing={0}
                              sx={{
                                display: 'flex',
                                maxWidth: '38%',
                                minWidth: '38%',
                              }}
                            >
                              <Stack
                                direction='row'
                                spacing={0}
                                sx={{ flexGrow: 1 }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    flexGrow: 1,
                                    textAlign: 'right',
                                    pr: 0.5,
                                  }}
                                >
                                  {delivered}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    width: '14%',
                                    textAlign: 'center',
                                  }}
                                >
                                  {'/'}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: 16,
                                    textAlign: 'right',
                                  }}
                                >
                                  {ordered}
                                </Typography>
                              </Stack>
                              <Typography
                                sx={{
                                  fontSize: 16,
                                  width: 40,
                                  textAlign: 'right',
                                  pr: 0.5,
                                }}
                              >
                                {t('bluu.quantityAbbr').toLowerCase()}
                              </Typography>
                            </Stack>
                          </Stack>

                          {/* Linje 2: kostpris */}
                          <Stack direction='row' spacing={0}>
                            <Typography
                              sx={{
                                flexGrow: 1,
                                fontSize: 16,
                                maxWidth: '62%',
                                minWidth: '62%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {line.deliveryDate
                                ? new Date(line.deliveryDate).toLocaleString()
                                : ''}
                            </Typography>
                            <Stack
                              direction='row'
                              spacing={0}
                              sx={{ maxWidth: '38%', minWidth: '38%' }}
                            >
                              <Typography
                                sx={{
                                  flexGrow: 1,
                                  fontSize: 16,
                                  textAlign: 'right',
                                }}
                              >
                                {formatCurrency(line.unitPrice)}
                              </Typography>
                            </Stack>
                          </Stack>

                          {/* Linje 3: utsalgspris */}
                          <Stack direction='row' spacing={0}>
                            <Typography
                              sx={{
                                fontSize: 16,
                                flexGrow: 1,
                                maxWidth: '62%',
                                minWidth: '62%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {/* tom kolonne venstre */}
                            </Typography>
                            <Stack
                              direction='row'
                              spacing={0}
                              sx={{ maxWidth: '38%', minWidth: '38%' }}
                            >
                              <Typography
                                sx={{
                                  flexGrow: 1,
                                  fontSize: 16,
                                  textAlign: 'right',
                                  color:
                                    (Number(line.unitPrice) || 0) >
                                    (Number(line.retailPrice) || 0)
                                      ? 'error.main'
                                      : 'primary.contrastText',
                                }}
                              >
                                {formatCurrency(line.retailPrice)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>

                    {/* Nederst: URL + status + "mangler"-info */}
                    <Grid item xs={12} sx={{ pl: 1, pb: 0.5, minWidth: 0 }}>
                      <Grid container alignItems='center' wrap='nowrap'>
                        <Grid item xs zeroMinWidth>
                          <a
                            href={line.vendorProductUrl ?? ''}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{
                              fontSize: 14,
                              letterSpacing: 1,
                              paddingLeft: 4,
                              display: 'block',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textDecoration: 'underline',
                              color:
                                (line.vendorProductUrl || '').length > 0
                                  ? 'lightblue'
                                  : 'lightgray',
                            }}
                          >
                            {(line.vendorProductUrl || '').length > 0
                              ? line.vendorProductUrl
                              : t('order.orderPost.noUrl')}
                          </a>
                        </Grid>

                        {/* Mangler + status (read-only) */}
                        <Grid item sx={{ ml: 2 }}>
                          <Typography sx={{ fontSize: 16 }}>
                            {t('Mangler')}: <b>{rest}</b>
                          </Typography>
                        </Grid>

                        <Grid item sx={{ ml: 1 }}>
                          <Chip
                            color={
                              done ? 'success' : partial ? 'warning' : 'default'
                            }
                            variant={done ? 'filled' : 'outlined'}
                            label={
                              done
                                ? t('Fullført')
                                : partial
                                ? t('Delvis mottatt')
                                : t('Ikke mottatt')
                            }
                            size='small'
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            );
          })}
        </Stack>
      </Paper>
    </Stack>
  );
};

/* -------------------------------------------------------------------------- */
/*                           READ-ONLY MAIN (HISTORY)                         */
/* -------------------------------------------------------------------------- */

export const OrderHistory = () => {
  const { height } = useWindowsDimension();
  const [state, dispatch] = useReducer(RootOrderReducer, initialRootOrderState);
  const [isReloadOrderedData, setIsReloadOrderedData] = useState<boolean>(true);
  const [owner] = useAtom(ownerAtom);
  const [user] = useAtom(userAtom);

  // PAGINATION
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // API (lese bare historikk)
  const isEnabled = isReloadOrderedData;
  const query = useGet(rq.getHistory(page, pageSize, isEnabled), );

  const onSelectOrder = (o: OrderedOrderHeaderViewModel) => {
    if (!o) {
      dispatch({
        type: 'ORDERED/SET_SELECTED_ORDER',
        payload: { order: null },
      });
      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR',
        payload: { vendor: null },
      });
      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR_CONTACT',
        payload: { vendorContact: null },
      });
      return;
    }
    dispatch({ type: 'ORDERED/SET_SELECTED_ORDER', payload: { order: o } });
    dispatch({
      type: 'ORDERED/SET_SELECTED_VENDOR',
      payload: { vendor: o.vendor ?? null },
    });
    dispatch({
      type: 'ORDERED/SET_SELECTED_VENDOR_CONTACT',
      payload: { vendorContact: o.vendorContact ?? null },
    });
  };

  useEffect(() => {
    if (query.isSuccess && query.data) {
      const page = query.data as OrderedPageViewModel;
      dispatch({ type: 'ORDERED/LOAD_PAGE', payload: page });
      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR',
        payload: { vendor: page.orders[0]?.vendor ?? null },
      });
      dispatch({
        type: 'ORDERED/SET_SELECTED_VENDOR_CONTACT',
        payload: { vendorContact: page.orders[0]?.vendorContact ?? null },
      });
      setIsReloadOrderedData(false);
    }
  }, [query.isSuccess, query.data]);

  return (
    <Grid container spacing={0} sx={{ display: 'flex', flexDirection: 'row' }}>
      {/* LEFT SIDE */}
      <Grid item xs={6} sx={{ pr: 0.5, m: 0 }}>
        <SearchFilter Icon={CreditCardIcon} Header='orders' />
        <List
          sx={{
            p: 2,
            height: Math.ceil(height - 242),
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          {state.ordered.orders.map((o) => (
            <OrderedLeftSideItem
              key={o.orderId}
              order={o}
              selectedOrderId={state.ordered.selectedOrder?.orderId ?? null}
              onSelectOrder={() => onSelectOrder(o)}
            />
          ))}
        </List>
        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      </Grid>

      {/* RIGHT SIDE (READ-ONLY) */}
      <Grid item xs={6} sx={{ pl: 0.5, m: 0 }}>
        <SearchFilter Icon={CreditCardIcon} Header='Selected order (history)' />
        <Grid
          container
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            p: 2,
            pb: 0,
            backgroundColor: 'primary.main',
          }}
        >
          <Grid item xs={7}>
            {state.ordered.selectedVendor && (
              <SingleOrderCompanyView company={state.ordered.selectedVendor} />
            )}
          </Grid>
          <Grid item xs={5}>
            {state.ordered.selectedVendorContact && (
              <SingleOrderEmployeeView
                employee={state.ordered.selectedVendorContact}
              />
            )}
          </Grid>

          {/* Header-summer (visning) */}
          <Grid
            item
            xs={12}
            sx={{
              m: 0,
              p: 0,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Typography sx={{ p: 2, fontWeight: 'bold', width: '25%' }}>
              Shipping Cost:
            </Typography>
            <Typography
              sx={{
                p: 2,
                width: '25%',
                fontWeight: 'bold',
                textAlign: 'right',
              }}
            >
              {'0.00'}
            </Typography>
            <Typography sx={{ p: 2, fontWeight: 'bold', width: '25%' }}>
              Total Cost:
            </Typography>
            <Typography
              sx={{
                p: 2,
                width: '25%',
                fontWeight: 'bold',
                textAlign: 'right',
              }}
            >
              {'0.00'}
            </Typography>
          </Grid>
        </Grid>

        <List
          sx={{
            p: 2,
            height: Math.ceil(height - 410),
            backgroundColor: 'primary.back',
            overflow: 'auto',
          }}
        >
          <OrderedHistoryRightSideItem order={state.ordered.selectedOrder} />
        </List>

        <Paper
          variant='elevation3'
          sx={{
            p: 2,
            height: 64,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
        />
      </Grid>
    </Grid>
  );
};
